import type { ChatClient } from '@cortex/sdk-chat-client';
import type { ChatHost } from './host.tsx';
import type { Block } from './model.ts';

// The chat's SAVED REPORTS — PORTED from the old chat.html (autoSaveReport, openReport, buildShareUrl,
// generateDashboard) and assistant/reports.js: a turn that showed at least one chart is saved on its
// own as a report (the question, the answer, every chart of the turn), the rail lists them, and a
// report opens as its own popup page (ChatReportPage). "📊 Generate dashboard" builds a LIVE dashboard
// from the charts that carry their SELECT (the host's `generateDashboard`, e.g. its reports module).

export interface ChatReportSummary {
  id: string;
  createdAt: string;
  project: string;
  title: string;
  chartCount: number;
}

/** A chart of the turn as the report keeps it: the block itself (a chart or a viz). */
export const reportChartOf = (b: Block): Record<string, unknown> => ({ ...b });

export const isReportChart = (b: Block) => b.type === 'chart' || b.type === 'viz';

export async function saveChatReport(
  api: ChatClient,
  body: { userPrompt?: string; answer: string; charts: Record<string, unknown>[] },
): Promise<{ id: string }> {
  const r = await api.POST('/reports', {
    body: {
      ...(body.userPrompt ? { userPrompt: body.userPrompt } : {}),
      answer: body.answer,
      charts: body.charts,
    },
  });
  if (!r.response.ok || !r.data) throw new Error(`HTTP ${r.response.status}`);
  return r.data;
}

export async function listChatReports(api: ChatClient): Promise<ChatReportSummary[]> {
  const r = await api.GET('/reports', {});
  return (r.data?.reports as ChatReportSummary[] | undefined) ?? [];
}

export async function deleteChatReport(api: ChatClient, id: string) {
  const r = await api.DELETE('/reports/{id}', { params: { path: { id } } });
  if (!r.response.ok) throw new Error(`HTTP ${r.response.status}`);
}

/** The report's own page (absolute: the Copy link target). */
export const reportUrl = (host: Pick<ChatHost, 'reportPath'>, slug: string, id: string) =>
  new URL(host.reportPath(slug, id), window.location.origin).toString();

/** Opens a report as a real OS popup window, separate from the chat (old openReport). */
export const openReport = (host: Pick<ChatHost, 'reportPath'>, slug: string, id: string) =>
  window.open(reportUrl(host, slug, id), '_blank', 'noopener,width=960,height=720');

export const hasSqlChart = (charts: Record<string, unknown>[]) =>
  charts.some((c) => c && typeof c.sql === 'string' && c.sql.trim());
